// Student Learning Profiles Configuration
// System configuration for SLP types, categories, and settings

export const slpConfig = {
  systemName: 'Student Learning Profiles',
  systemNameCh: '學生學習概覽',
  version: '1.0',
  academicYear: '2024-2025',
  schoolCode: 'POHL',
  maxStudentsPerEvent: 200,
  defaultDuration: 2, // hours
  requiresApproval: true
};

// SLP Types - Main categories
export const slpTypes = {
  ole: {
    id: 'ole',
    name: '活動經歷',
    nameEn: 'Other Learning Experiences (OLE)',
    description: '其他學習經歷活動記錄',
    color: '#3b82f6',
    icon: '📚',
    requiresHours: true,
    canConvertToAward: true
  },
  eca: {
    id: 'eca',
    name: '課外活動',
    nameEn: 'Extra-Curricular Activities (ECA)',
    description: '課外活動參與記錄',
    color: '#10b981',
    icon: '🎯',
    requiresHours: true,
    canConvertToAward: false
  },
  awards: {
    id: 'awards',
    name: '獎項成就',
    nameEn: 'Awards',
    description: '校外獎項及成就記錄',
    color: '#f59e0b',
    icon: '🏆',
    requiresHours: false,
    canConvertToAward: false
  },
  achievements: {
    id: 'achievements',
    name: '獎項成績',
    nameEn: 'Achievements',
    description: '校內獎項及成績記錄',
    color: '#8b5cf6',
    icon: '🌟',
    requiresHours: false,
    canConvertToAward: false
  }
};

// Event Types - Sub-categories for OLE
export const eventTypes = {
  academic: {
    id: 'academic',
    name: '學術',
    nameEn: 'Academic',
    description: '學術相關活動',
    color: '#3b82f6',
    icon: '📖',
    examples: ['學術比賽', '研習營', '講座', '工作坊']
  },
  occupational: {
    id: 'occupational',
    name: '職業',
    nameEn: 'Occupational',
    description: '職業體驗及探索',
    color: '#6b7280',
    icon: '💼',
    examples: ['職業體驗', '實習', '工作影子', '職業講座']
  },
  sports: {
    id: 'sports',
    name: '體育',
    nameEn: 'Sports',
    description: '體育運動活動',
    color: '#ef4444',
    icon: '⚽',
    examples: ['運動比賽', '體育訓練', '運動會', '健身活動']
  },
  arts: {
    id: 'arts',
    name: '藝術',
    nameEn: 'Arts',
    description: '藝術文化活動',
    color: '#ec4899',
    icon: '🎨',
    examples: ['音樂表演', '戲劇', '美術展覽', '創作活動']
  },
  social: {
    id: 'social',
    name: '社會活動',
    nameEn: 'Social Activities',
    description: '社會服務及活動',
    color: '#06b6d4',
    icon: '🤝',
    examples: ['義工服務', '社區活動', '慈善活動', '環保活動']
  },
  cultural: {
    id: 'cultural',
    name: '文化活動',
    nameEn: 'Cultural Activities',
    description: '文化交流及體驗',
    color: '#8b5cf6',
    icon: '🏛️',
    examples: ['文化交流', '傳統節慶', '博物館參觀', '文化表演']
  },
  training: {
    id: 'training',
    name: '培訓',
    nameEn: 'Training',
    description: '技能培訓及發展',
    color: '#f59e0b',
    icon: '🎯',
    examples: ['領袖訓練', '技能課程', '證書課程', '專業培訓']
  },
  travel: {
    id: 'travel',
    name: '遊學/參觀',
    nameEn: 'Travel / Site Visit',
    description: '遊學團及實地考察',
    color: '#10b981',
    icon: '✈️',
    examples: ['遊學團', '實地考察', '文化之旅', '教育旅行']
  }
};

// SLP Categories for detailed classification
export const slpCategories = {
  duration: {
    short: { id: 'short', name: '短期', range: '1-4小時' },
    medium: { id: 'medium', name: '中期', range: '5-20小時' },
    long: { id: 'long', name: '長期', range: '21小時以上' }
  },
  level: {
    school: { id: 'school', name: '校內', nameEn: 'School Level' },
    district: { id: 'district', name: '區內', nameEn: 'District Level' },
    territory: { id: 'territory', name: '全港', nameEn: 'Territory Level' },
    national: { id: 'national', name: '全國', nameEn: 'National Level' },
    international: { id: 'international', name: '國際', nameEn: 'International Level' }
  },
  role: {
    participant: { id: 'participant', name: '參加者', nameEn: 'Participant' },
    organizer: { id: 'organizer', name: '籌辦者', nameEn: 'Organizer' },
    leader: { id: 'leader', name: '領袖', nameEn: 'Leader' },
    volunteer: { id: 'volunteer', name: '義工', nameEn: 'Volunteer' }
  }
};

// Achievement Levels for Awards and Achievements
export const achievementLevels = {
  gold: {
    id: 'gold',
    name: '金獎',
    nameEn: 'Gold Award',
    color: '#fbbf24',
    icon: '🥇',
    points: 10
  },
  silver: {
    id: 'silver',
    name: '銀獎',
    nameEn: 'Silver Award',
    color: '#9ca3af',
    icon: '🥈',
    points: 8
  },
  bronze: {
    id: 'bronze',
    name: '銅獎',
    nameEn: 'Bronze Award',
    color: '#cd7c2f',
    icon: '🥉',
    points: 6
  },
  merit: {
    id: 'merit',
    name: '優異獎',
    nameEn: 'Merit Award',
    color: '#10b981',
    icon: '🏅',
    points: 5
  },
  participation: {
    id: 'participation',
    name: '參與獎',
    nameEn: 'Participation Award',
    color: '#6b7280',
    icon: '🎖️',
    points: 3
  },
  certificate: {
    id: 'certificate',
    name: '證書',
    nameEn: 'Certificate',
    color: '#3b82f6',
    icon: '📜',
    points: 4
  }
};

// Organization Types
export const organizationTypes = {
  government: {
    id: 'government',
    name: '政府機構',
    nameEn: 'Government Organization',
    examples: ['教育局', '民政事務總署', '康樂及文化事務署']
  },
  ngo: {
    id: 'ngo',
    name: '非牟利機構',
    nameEn: 'Non-profit Organization',
    examples: ['香港青年協會', '香港童軍總會', '香港紅十字會']
  },
  school: {
    id: 'school',
    name: '學校',
    nameEn: 'School',
    examples: ['本校', '友校', '大學']
  },
  commercial: {
    id: 'commercial',
    name: '商業機構',
    nameEn: 'Commercial Organization',
    examples: ['銀行', '科技公司', '媒體機構']
  },
  international: {
    id: 'international',
    name: '國際組織',
    nameEn: 'International Organization',
    examples: ['聯合國', '國際奧委會', '國際青年組織']
  }
};

// System Settings
export const systemSettings = {
  slpNumberFormat: 'YYYYMMDDNNN', // Year+Month+Day+3-digit sequence
  requiredFields: ['date', 'slpType', 'eventType', 'organization', 'venue', 'students'],
  optionalFields: ['timeFrom', 'timeTo', 'teachersInCharge', 'remarks'],
  autoApproval: false,
  notificationEnabled: true,
  exportFormats: ['PDF', 'Excel', 'CSV'],
  maxFileSize: '10MB',
  allowedFileTypes: ['.jpg', '.png', '.pdf', '.doc', '.docx']
};

// Form Validation Rules
export const validationRules = {
  slpNumber: {
    required: true,
    pattern: /^\d{11}$/,
    message: 'SLP編號必須為11位數字'
  },
  date: {
    required: true,
    type: 'date',
    message: '請選擇有效日期'
  },
  organization: {
    required: true,
    minLength: 2,
    maxLength: 100,
    message: '機構名稱必須為2-100字符'
  },
  venue: {
    required: true,
    minLength: 2,
    maxLength: 100,
    message: '地點必須為2-100字符'
  },
  students: {
    required: true,
    minItems: 1,
    message: '至少需要選擇一名學生'
  },
  duration: {
    min: 0.5,
    max: 24,
    message: '活動時間必須在0.5-24小時之間'
  }
}; 